import {
  TBrowserData,
  TEventMetaData,
  TEventPageData,
  TEventSessionData,
  TMableEvent,
  TPlatformIdentifiers,
} from '../constants/event_types/base_mable_event';
import { EMableEventName } from '../constants/event_types/events';
import { TCustomerDataArrays } from '../data_handlers/customer_data_new/types';
import { TimeUtils } from './utils/time_utils';
import { UniqueIdUtils } from './utils/unique_id_utils';

/**
 * Determines the final timestamp for an event
 *
 * @param uaHeaders - The user agent headers
 * @param serverTime - The synchronized server time
 * @returns The final timestamp in seconds
 */
function getFinalTimestamp(
  uaHeaders: Record<string, string>,
  serverTime: number,
): number {
  let finalTimestamp = 0;

  if (uaHeaders['x-cpx-timestamp']) {
    const cpxTimestamp = Number(uaHeaders['x-cpx-timestamp']);
    const threeMinutesInSeconds = 3 * 60;
    const timestampDiff = Math.floor(serverTime) - cpxTimestamp;

    if (timestampDiff > threeMinutesInSeconds) {
      // CPX timestamp is too old, use server time
      finalTimestamp = Math.floor(serverTime);
    } else {
      finalTimestamp = cpxTimestamp;
    }
  } else {
    finalTimestamp = Math.floor(serverTime);
  }

  // if final timestamp is in ms converted to seconds
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  if (finalTimestamp > 1_000_000_000_000) {
    finalTimestamp = Math.floor(finalTimestamp / 1000);
  }

  // Only convert from milliseconds to seconds if the timestamp is in milliseconds
  // (Unix timestamps in milliseconds are typically 13 digits long)
  // if (finalTimestamp > 1_000_000_000_000) {
  //   finalTimestamp = Math.floor(finalTimestamp / 1000);
  // }

  return finalTimestamp;
}

/**
 * Placeholder function for composing a generic MableEvent
 *
 * @param eventName - The name of the event
 * @param data - Additional data for the event
 * @param sessionData - Session data
 * @param normalisedCustomerData - Normalised user data
 * @param ipLocationData - IP locationthe data
 * @param browserData - Browser data
 * @returns A generic MableEvent object
 */
// eslint-disable-next-line @typescript-eslint/max-params
export async function composeGenericMableEvent(
  eventName: EMableEventName,
  sessionData: TEventSessionData | any,
  browserInstanceData: TBrowserData,
  userData: TCustomerDataArrays,
  platformIdentifiers: TPlatformIdentifiers,
  pageData: TEventPageData,
  metaData: TEventMetaData,
  identityId: string,
  uaHeaders: Record<string, string>,
): Promise<TMableEvent | any> {
  if (sessionData.cdfs) {
    delete sessionData.cdfs;
  }
  if (sessionData.cdas) {
    // TODO: this should not be required -> fix the root cause
    delete sessionData.cdas;
  }
  if (sessionData.pids) {
    delete sessionData.pids;
  }

  // Get synchronized time information
  const timeInfo = await TimeUtils.getCurrentSyncedTime();
  const serverTime = timeInfo?.syncedTime ?? Date.now();

  // Get the final timestamp
  const finalTimestamp = getFinalTimestamp(uaHeaders, serverTime);

  // Add time-related information to UA headers

  // Add time-related information to UA headers
  const enhancedUaHeaders = {
    ...uaHeaders,
    'x-client-ip': timeInfo?.headers?.['x-client-ip'] || '',
    'x-client-ip-v4': timeInfo?.headers?.['x-client-ip-v4'] || '',
    'x-client-ip-v6': timeInfo?.headers?.['x-client-ip-v6'] || '',
    'x-client-server-time-diff': timeInfo?.clientServerDiff?.toString() ?? '0',
    'x-client-time': timeInfo?.clientTime?.toString() ?? Date.now().toString(),
    'x-cpx-event-id': uaHeaders['x-cpx-event-id'] || '',
    'x-forwarded-for': timeInfo?.headers?.['x-forwarded-for'] || '',
    'x-real-ip': timeInfo?.headers?.['x-real-ip'] || '',
    'x-request-duration': timeInfo?.requestDuration?.toFixed(2) ?? '0',
    'x-server-time': serverTime.toString() ?? '0',
  };

  const baseMableEvent: TMableEvent = {
    bd: browserInstanceData,
    cdas: userData,
    eid: uaHeaders['event-id'] || UniqueIdUtils.getUuidV7(),
    en: eventName,
    ii: identityId, // identity id
    md: metaData,
    pd: pageData,
    pids: platformIdentifiers,
    sd: sessionData,
    ts: finalTimestamp, // Use CPX timestamp if available, otherwise use server time
    uaHeaders: enhancedUaHeaders,
  };

  return baseMableEvent;
}
